import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Heart,
  Shield,
  Plus,
  Settings,
  Eye,
  EyeOff,
  Calendar,
  Activity,
  User,
  LogOut,
  Crown,
  RefreshCw,
  Check,
  X,
  BarChart3,
  Clock,
  Scan
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { getUserProfile } from '../../services/auth/googleAuth';
import { getWebIDsByUser } from '../../services/webids';
import { getUserActionLogs, getAccessLogs } from '../../services/accessLogs';
import { Button } from '../ui';
import { CopyText } from '../ui/CopyButton';
import LoadingSpinner from '../ui/LoadingSpinner';

/**
 * @component UserDashboard
 * @description Dashboard personal del usuario para gestionar sus perfiles de emergencia
 * Usa terminología "Perfiles de Emergencia" en lugar de "WebIDs"
 */

interface DashboardState {
  loading: boolean;
  userProfile: any;
  emergencyProfiles: any[];
  recentActivity: any[];
  scanStats: Record<string, number>;
  error?: string;
  successMessage?: string;
  lastUpdated?: Date;
}

const UserDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [state, setState] = useState<DashboardState>({
    loading: true,
    userProfile: null,
    emergencyProfiles: [],
    recentActivity: [],
    scanStats: {},
  });

  // Manejar mensajes de éxito de la navegación
  useEffect(() => {
    const locationState = location.state as any;
    if (locationState?.message && locationState?.type === 'success') {
      setState(prev => ({ ...prev, successMessage: locationState.message }));

      // Limpiar el mensaje después de 5 segundos
      const timer = setTimeout(() => {
        setState(prev => ({ ...prev, successMessage: undefined }));
      }, 5000);

      // Limpiar el state de la navegación
      navigate(location.pathname, { replace: true });

      return () => clearTimeout(timer);
    }
  }, [location.state, navigate, location.pathname]);

  // Cargar datos del usuario con sincronización mejorada
  const loadUserData = async () => {
    if (!user) {
      navigate('/');
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: undefined }));

      // Cargar datos en paralelo para mejor rendimiento
      const results = await Promise.allSettled([
        getUserProfile(user.uid),
        getWebIDsByUser(user.uid),
        getUserActionLogs(user.uid, 7).catch(error => {
          console.warn('No se pudieron cargar los logs de actividad (índice requerido):', error.message);
          return []; // Retornar array vacío si falla
        })
      ]);

      const userProfile = results[0].status === 'fulfilled' ? results[0].value : null;
      const webIds = results[1].status === 'fulfilled' ? results[1].value : [];
      const recentActivity = results[2].status === 'fulfilled' ? results[2].value : [];

      // Calcular estadísticas de escaneo para cada WebID
      const scanStats: Record<string, number> = {};
      if (webIds.length > 0) {
        // Obtener logs de acceso para cada WebID con manejo de errores mejorado
        const accessResults = await Promise.allSettled(
          webIds.map(webId =>
            getAccessLogs(webId.id, 30).catch(error => {
              console.warn(`No se pudieron cargar los logs de acceso para WebID ${webId.id} (índice requerido):`, error.message);
              return []; // Retornar array vacío si falla
            })
          )
        );

        webIds.forEach((webId, index) => {
          if (accessResults[index].status === 'fulfilled') {
            const logs = accessResults[index].value as any[];
            scanStats[webId.id] = logs.filter(log => log.accessType === 'scan').length;
          } else {
            scanStats[webId.id] = 0;
          }
        });
      }

      setState({
        loading: false,
        userProfile,
        emergencyProfiles: webIds,
        recentActivity,
        scanStats,
        lastUpdated: new Date(),
        error: results[0].status === 'rejected' ? 'Error al cargar el perfil de usuario' : undefined
      });

    } catch (error) {
      console.error('Error cargando datos del usuario:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Error al cargar tus datos. Por favor, recarga la página.'
      }));
    }
  };

  useEffect(() => {
    loadUserData();

    // Configurar actualización automática cada 5 minutos
    const interval = setInterval(loadUserData, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user, navigate]);

  // Función para recargar datos manualmente
  const refreshData = async () => {
    await loadUserData();
    console.log('✅ Datos recargados exitosamente');
  };

  // Manejar logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  // Obtener estadísticas
  const getStats = () => {
    const total = state.emergencyProfiles.length;
    const active = state.emergencyProfiles.filter((p: any) => p.activationStatus === 'onboarding_completed').length;
    const public_ = state.emergencyProfiles.filter((p: any) => p.emergencyProfile?.isPublic).length;

    return { total, active, public: public_ };
  };

  // Mostrar error si existe
  if (state.error && !state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-sm max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error al cargar el dashboard</h2>
          <p className="text-gray-600 mb-6">{state.error}</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={refreshData} className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Reintentar
            </Button>
            <Button variant="outline" onClick={() => navigate('/')}>
              Ir al inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Cargando tu dashboard..." />
      </div>
    );
  }

  const stats = getStats();

  // Funciones auxiliares para formatear actividad
  const getActivityDescription = (activity: any) => {
    switch (activity.action) {
      case 'create_webid':
        return 'Nuevo perfil de emergencia creado';
      case 'update_emergency_profile':
        return 'Perfil de emergencia actualizado';
      case 'scan':
        return `Perfil escaneado (${activity.resourceId || 'ID no disponible'})`;
      case 'login':
        return 'Inicio de sesión';
      case 'logout':
        return 'Cierre de sesión';
      default:
        return `Acción: ${activity.action}`;
    }
  };

  const formatActivityTime = (timestamp: any) => {
    if (!timestamp) return 'Fecha no disponible';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Hace un momento';
    if (diffMins < 60) return `Hace ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
    if (diffHours < 24) return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;

    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-sos-sticky">
      {/* Header Optimizado */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 lg:py-6">
          {/* Título y Logo NFCores */}
          <div className="flex items-center justify-center mb-4 lg:mb-6">
            <div className="flex items-center gap-3">
              <img
                src="/assets/images/NFCores-original.png"
                alt="NFCores"
                className="h-6 lg:h-8 w-auto"
              />
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Panel NFCores</h1>
            </div>
          </div>

          {/* Vista Desktop */}
          <div className="hidden lg:flex items-center justify-between">
            <div className="flex items-center gap-4">
              {user?.photoURL && (
                <img
                  src={user.photoURL}
                  alt="Foto de perfil"
                  className="w-12 h-12 rounded-full"
                />
              )}
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Hola, {user?.displayName || 'Usuario'}
                  </h2>
                  {user?.role === 'admin' && (
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 text-xs font-semibold rounded-full border border-purple-200">
                      <Crown className="h-3 w-3" />
                      Administrador
                    </div>
                  )}
                </div>
                <p className="text-gray-600">
                  {user?.role === 'admin'
                    ? 'Dashboard personal - Gestiona tus perfiles de emergencia'
                    : 'Gestiona tus perfiles de emergencia'
                  }
                </p>
                {state.lastUpdated && (
                  <p className="text-xs text-gray-500 mt-1">
                    Última actualización: {state.lastUpdated.toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Botón de recarga de datos */}
              <Button
                variant="ghost"
                size="sm"
                onClick={refreshData}
                disabled={state.loading}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700"
                title="Actualizar datos"
              >
                <RefreshCw className={`h-4 w-4 ${state.loading ? 'animate-spin' : ''}`} />
              </Button>

              {/* Botón de Panel de Admin - Solo visible para administradores */}
              {user?.role === 'admin' && (
                <Button
                  onClick={() => navigate('/admin')}
                  className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white border-0"
                >
                  <Crown className="h-4 w-4" />
                  Panel de Admin
                </Button>
              )}

              <Button
                variant="outline"
                onClick={() => navigate('/settings')}
                className="flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                Configuración
              </Button>

              <Button
                variant="outline"
                onClick={handleLogout}
                className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4" />
                Cerrar Sesión
              </Button>
            </div>
          </div>

          {/* Vista Móvil */}
          <div className="lg:hidden space-y-4">
            {/* Información del usuario */}
            <div className="flex items-center gap-3">
              {user?.photoURL && (
                <img
                  src={user.photoURL}
                  alt="Foto de perfil"
                  className="w-10 h-10 rounded-full"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-lg font-bold text-gray-900 truncate">
                    Hola, {user?.displayName || 'Usuario'}
                  </h2>
                  {user?.role === 'admin' && (
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 text-xs font-semibold rounded-full border border-purple-200 flex-shrink-0">
                      <Crown className="h-3 w-3" />
                      Admin
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-600 truncate">
                  {user?.role === 'admin'
                    ? 'Dashboard personal'
                    : 'Gestiona tus perfiles'
                  }
                </p>
                {state.lastUpdated && (
                  <p className="text-xs text-gray-500">
                    Actualizado: {state.lastUpdated.toLocaleTimeString()}
                  </p>
                )}
              </div>
              {/* Botón de recarga móvil */}
              <button
                onClick={refreshData}
                disabled={state.loading}
                className="flex items-center justify-center w-10 h-10 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                title="Actualizar datos"
              >
                <RefreshCw className={`h-4 w-4 ${state.loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Botones de acción móvil */}
            <div className="grid grid-cols-2 gap-2">
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  className="inline-flex items-center justify-center px-3 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-sm font-medium rounded-md min-h-[44px]"
                >
                  <Crown className="h-4 w-4 mr-1" />
                  Panel Admin
                </button>
              )}

              <button
                onClick={() => navigate('/settings')}
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 min-h-[44px]"
              >
                <Settings className="h-4 w-4 mr-1" />
                Configuración
              </button>

              <button
                onClick={handleLogout}
                className={`inline-flex items-center justify-center px-3 py-2 border border-red-200 text-sm font-medium rounded-md text-red-600 bg-red-50 hover:bg-red-100 min-h-[44px] ${
                  user?.role === 'admin' ? '' : 'col-span-2'
                }`}
              >
                <LogOut className="h-4 w-4 mr-1" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mensaje de éxito */}
      {state.successMessage && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-green-800 font-medium">{state.successMessage}</p>
            </div>
            <button
              onClick={() => setState(prev => ({ ...prev, successMessage: undefined }))}
              className="text-green-600 hover:text-green-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <Heart className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-gray-600">Perfiles Totales</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                <p className="text-gray-600">Perfiles Activos</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Eye className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.public}</p>
                <p className="text-gray-600">Perfiles Públicos</p>
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Profiles List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Mis Perfiles de Emergencia
              </h2>
              <Button
                onClick={() => navigate('/tutorial-activacion')}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Activar Nuevo Perfil
              </Button>
            </div>
          </div>

          {state.emergencyProfiles.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No tienes perfiles de emergencia
              </h3>
              <p className="text-gray-600 mb-6">
                Activa tu primer perfil de emergencia para comenzar
              </p>
              <Button
                onClick={() => navigate('/tutorial-activacion')}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Activar Mi Primer Perfil
              </Button>
            </div>
          ) : (
            <>
              {/* Vista Desktop */}
              <div className="hidden lg:block divide-y divide-gray-200">
                {state.emergencyProfiles.map((profile) => (
                  <div key={profile.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Logo SOS NFC */}
                        <div className="w-16 h-16 bg-red-50 rounded-lg flex items-center justify-center border border-red-200">
                          <img
                            src="/assets/images/SOS NFC-red.png"
                            alt="SOS NFC"
                            className="h-10 w-auto"
                            onError={(e) => {
                              // Fallback si no se encuentra la imagen
                              const target = e.currentTarget as HTMLImageElement;
                              const fallback = target.nextElementSibling as HTMLElement;
                              target.style.display = 'none';
                              if (fallback) {
                                fallback.style.display = 'flex';
                              }
                            }}
                          />
                          <div className="hidden w-full h-full items-center justify-center">
                            <Heart className="h-8 w-8 text-red-600" />
                          </div>
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-semibold text-gray-900">
                              {profile.emergencyProfile?.fullName || 'Sin nombre'}
                            </h3>
                            {/* Contador de escaneos */}
                            <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-full">
                              <Scan className="h-3 w-3 text-blue-600" />
                              <span className="text-xs font-medium text-blue-600">
                                {state.scanStats[profile.id] || 0} escaneos
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 mt-1">
                            <CopyText
                              text={profile.id}
                              displayText={`ID: ${profile.id}`}
                              textClassName="text-sm text-gray-500"
                              buttonVariant="ghost"
                            />

                            <div className="flex items-center gap-2">
                              {profile.emergencyProfile?.isPublic ? (
                                <>
                                  <Eye className="h-4 w-4 text-green-600" />
                                  <span className="text-sm text-green-600">Público</span>
                                </>
                              ) : (
                                <>
                                  <EyeOff className="h-4 w-4 text-gray-400" />
                                  <span className="text-sm text-gray-500">Privado</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            profile.activationStatus === 'onboarding_completed'
                              ? 'bg-green-100 text-green-800'
                              : profile.activationStatus === 'activated'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {profile.activationStatus === 'onboarding_completed'
                              ? 'Completado'
                              : profile.activationStatus === 'activated'
                              ? 'Pendiente Configuración'
                              : 'No Activado'
                            }
                          </div>

                          {profile.onboardingCompletedAt && (
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(profile.onboardingCompletedAt.toDate()).toLocaleDateString()}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          {profile.activationStatus === 'activated' && (
                            <Button
                              size="sm"
                              onClick={() => navigate(`/onboarding/${profile.id}`)}
                              className="flex items-center gap-1"
                            >
                              <Settings className="h-3 w-3" />
                              Configurar
                            </Button>
                          )}

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/w/${profile.id}`)}
                            className="flex items-center gap-1"
                          >
                            <Eye className="h-3 w-3" />
                            Ver Perfil Público
                          </Button>

                          {profile.activationStatus === 'onboarding_completed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/dashboard/profile/${profile.id}/edit`)}
                              className="flex items-center gap-1"
                            >
                              <Settings className="h-3 w-3" />
                              Editar Perfil
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Vista Móvil (Estilo Admin) */}
              <div className="lg:hidden p-4 space-y-4">
                {state.emergencyProfiles.map((profile) => (
                  <div key={profile.id} className="bg-white rounded-lg shadow p-4">
                    {/* Header de la card */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {/* Logo SOS NFC */}
                        <div className="h-10 w-10 bg-red-50 rounded-lg flex items-center justify-center border border-red-200">
                          <img
                            src="/assets/images/SOS NFC-red.png"
                            alt="SOS NFC"
                            className="h-6 w-auto"
                            onError={(e) => {
                              const target = e.currentTarget as HTMLImageElement;
                              const fallback = target.nextElementSibling as HTMLElement;
                              target.style.display = 'none';
                              if (fallback) {
                                fallback.style.display = 'flex';
                              }
                            }}
                          />
                          <div className="hidden w-full h-full items-center justify-center">
                            <Heart className="h-5 w-5 text-red-600" />
                          </div>
                        </div>
                        <div>
                          <CopyText
                            text={profile.id}
                            truncate={true}
                            maxLength={8}
                            textClassName="text-base font-medium text-gray-900"
                            buttonVariant="ghost"
                            onCopy={() => console.log(`WebID ${profile.id} copiado`)}
                          />
                        </div>
                      </div>
                      {/* Contador de escaneos */}
                      <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-full">
                        <Scan className="h-3 w-3 text-blue-600" />
                        <span className="text-xs font-medium text-blue-600">
                          {state.scanStats[profile.id] || 0}
                        </span>
                      </div>
                    </div>

                    {/* Badges de estado */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        profile.activationStatus === 'onboarding_completed'
                          ? 'bg-green-100 text-green-800'
                          : profile.activationStatus === 'activated'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {profile.activationStatus === 'onboarding_completed'
                          ? 'Completado'
                          : profile.activationStatus === 'activated'
                          ? 'Pendiente Configuración'
                          : 'No Activado'
                        }
                      </span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        profile.emergencyProfile?.isPublic
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {profile.emergencyProfile?.isPublic ? 'Público' : 'Privado'}
                      </span>
                    </div>

                    {/* Información del perfil */}
                    {profile.emergencyProfile && (
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <h5 className="text-sm font-medium text-gray-900 mb-2">
                          Perfil de Emergencia
                        </h5>
                        <div className="space-y-1 text-sm">
                          <div>
                            <span className="text-gray-500">Nombre:</span>
                            <span className="ml-2 text-gray-900">
                              {profile.emergencyProfile.fullName || 'Sin nombre'}
                            </span>
                          </div>
                          {profile.emergencyProfile.phone && (
                            <div>
                              <span className="text-gray-500">Teléfono:</span>
                              <span className="ml-2 text-gray-900">{profile.emergencyProfile.phone}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-500">Creado:</span>
                            <span className="ml-2 text-gray-900">
                              {profile.createdAt?.toDate?.()?.toLocaleDateString() || 'Fecha no disponible'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Botones de acción móvil */}
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => navigate(`/w/${profile.id}`)}
                        className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 min-h-[44px]"
                        title="Ver perfil público"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Ver Perfil
                      </button>

                      {profile.activationStatus === 'activated' ? (
                        <button
                          onClick={() => navigate(`/onboarding/${profile.id}`)}
                          className="inline-flex items-center justify-center px-3 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 min-h-[44px]"
                          title="Configurar perfil"
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Configurar
                        </button>
                      ) : profile.activationStatus === 'onboarding_completed' ? (
                        <button
                          onClick={() => navigate(`/dashboard/profile/${profile.id}/edit`)}
                          className="inline-flex items-center justify-center px-3 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 min-h-[44px]"
                          title="Editar perfil"
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Editar
                        </button>
                      ) : (
                        <button
                          disabled
                          className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-400 bg-gray-50 min-h-[44px]"
                          title="Perfil no activado"
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          No Disponible
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Activity className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Actividad Reciente</h3>
            </div>

            {state.recentActivity.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-600 text-sm mb-2">
                  No hay actividad reciente registrada
                </p>
                <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                  💡 Algunas funciones requieren configuración de índices en Firebase
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {state.recentActivity.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      {activity.action === 'create_webid' && <Plus className="h-4 w-4 text-blue-600" />}
                      {activity.action === 'update_emergency_profile' && <Settings className="h-4 w-4 text-blue-600" />}
                      {activity.action === 'scan' && <Scan className="h-4 w-4 text-blue-600" />}
                      {!['create_webid', 'update_emergency_profile', 'scan'].includes(activity.action) && (
                        <Activity className="h-4 w-4 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {getActivityDescription(activity)}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <p className="text-xs text-gray-500">
                          {formatActivityTime(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {state.recentActivity.length > 5 && (
                  <p className="text-xs text-gray-500 text-center mt-3">
                    Mostrando las 5 actividades más recientes
                  </p>
                )}
              </div>
            )}
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Privacidad y Seguridad</h3>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Gestiona la privacidad de tus datos médicos
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/privacy')}
            >
              Configurar Privacidad
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
